import { StreamStatusBadge } from "@/modules/camera-stream/components/StreamStatusBadge";
import { pickDisplayCropRows } from "@/modules/camera-stream/utils/decodeTimestampBurnin";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useWebRTCStream } from "../../hooks/useWebRTCStream";

interface CameraDisplayProps {
  selectedMxid: string;
  selectedSensorName: string;
  onStreamingChange?: (isStreaming: boolean) => void;
  onMediaStreamChange?: (stream: MediaStream | null) => void;
  isRecording?: boolean;
  /** Device is rebooting — the stream is expected to be gone. */
  isRebooting?: boolean;
  rebootSecondsLeft?: number;
}

export const CameraDisplay = ({
  selectedMxid,
  selectedSensorName,
  onStreamingChange,
  onMediaStreamChange,
  isRecording,
  isRebooting = false,
  rebootSecondsLeft = 0,
}: CameraDisplayProps) => {
  const { t } = useTranslation("capture");
  const { videoRef, isStreaming, error, replayEnded, isReplay } = useWebRTCStream({
    selectedMxid,
    selectedSensorName,
    onMediaStreamChange,
  });

  const [aspectRatio, setAspectRatio] = useState("16/9");
  const [sourceSize, setSourceSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video && video.videoWidth && video.videoHeight) {
      setAspectRatio(`${video.videoWidth}/${video.videoHeight}`);
      setSourceSize({ width: video.videoWidth, height: video.videoHeight });
    }
  }, [videoRef]);

  useEffect(() => {
    onStreamingChange?.(isStreaming);
  }, [isStreaming, onStreamingChange]);

  return (
    <div className="relative">
      {(isStreaming || isRebooting) && (
        <div className="absolute left-4 top-4 z-10">
          <StreamStatusBadge
            variant={
              isRebooting ? "offline" : isReplay ? "replay" : "live"
            }
          />
        </div>
      )}

      {isRecording && (
        <div className="absolute right-4 top-4 z-10">
          <StreamStatusBadge variant="recording" />
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        style={{
          aspectRatio,
          ...(sourceSize && {
            clipPath: `inset(${(pickDisplayCropRows(sourceSize.width) / sourceSize.height) * 100}% 0 0 0)`,
          }),
        }}
        className="w-full rounded-md bg-black/5"
      ></video>

      {!isRebooting && replayEnded && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {t("stream.replayFinished")}
        </div>
      )}
      {!isRebooting && !replayEnded && !isStreaming && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {t("stream.loading")}
        </div>
      )}
      {!isRebooting && !replayEnded && error && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-destructive">
          {t("stream.error", { error })}
        </div>
      )}

      {isRebooting && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-md bg-neutral-950/90 p-6 text-center">
          <Spinner className="size-8 text-white" />
          <p className="text-xl font-bold text-white">
            {t("stream.rebooting.title")}
          </p>
          <p className="text-sm text-white/60">
            <Trans
              t={t}
              i18nKey="stream.rebooting.countdown"
              values={{ seconds: rebootSecondsLeft }}
              components={{ highlight: <span className="text-white" /> }}
            />
          </p>
        </div>
      )}
    </div>
  );
};
